import type { ReactNode } from "react";
import { createPortal } from "react-dom";

import type { DisplayLoyaltyTier } from "@/api/orval/model";
import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";
import { DraggableModal } from "atoms/draggable-modal/DraggableModal";

import { LoyaltyTierBadge } from "./LoyaltyTierBadge";

type TierSheetProps = {
  readonly open: boolean;
  readonly tier: DisplayLoyaltyTier;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
};

/** One tier up close: its medallion and name, then what the caller says about it. */
export const TierSheet = ({ open, tier, title, onClose, children }: TierSheetProps) => {
  const { t } = useTranslation();

  return createPortal(
    <div
      style={{ containerType: "inline-size", containerName: "app" }}
      className="pointer-events-none fixed inset-0 z-[60] [&>*:first-child]:pointer-events-auto"
    >
      <DraggableModal
        open={open}
        onClose={onClose}
        header={
          <h2 className="flex w-full items-center justify-center border-b border-light-grey pb-5 pt-6 font-sans text-bold2 uppercase tracking-[.04em] text-primary">
            {title}
          </h2>
        }
        footer={
          <div className="px-5 py-4">
            <Button fullWidth onClick={onClose}>
              {t("loyalty.gotIt")}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-5 px-5 pb-2 pt-5 text-left">
          <div className="flex items-center gap-3">
            <LoyaltyTierBadge tier={tier} size="md" />
            <span className="text-sub2 text-primary">{tier.name}</span>
          </div>
          {children}
        </div>
      </DraggableModal>
    </div>,
    document.body,
  );
};
